import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import Dropdown from 'antd/es/dropdown';
import Menu from 'antd/es/menu';
import Form from 'antd/es/form';
import Spin from 'antd/es/spin';
import Cascader from 'antd/es/cascader';

import ProcessTagsService from 'services/processTags';
import useForm, { FormMode } from 'hooks/useForm';

import { Row, Col } from 'components/layout';
import { Text } from 'components/Text';
import TimelineBar from 'components/TimelineBar';
import FormModal from 'components/FormModal';
// import Select from 'components/Select';
import Icon from 'components/Icon';

import { dateMonthDayFormat, timeFormat } from 'config/common';
import { formatDate, toTimezone } from 'helpers/date';

const timelineColors = {
  black: '#232526',
  green: '#52C41A',
  orange: '#FAAD14',
  yellow: '#FAAD14',
  red: '#F5222D',
  blue: '#42B0D5',
  empty: '#E4E8ED',
};

const TimelineLabel = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  transform: translateX(-50%);
  text-align: center;

  &:first-child {
    transform: unset;
    text-align: left;
  }

  &:last-child {
    transform: translateX(-100%);
    text-align: right;
  }
`;

const SegmentTooltipSpin = styled(Spin)`
  width: 100%;
  margin-top: 2px;

  & .ant-spin-dot-item {
    background-color: #fff;
  }
`;

const Stripes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  pointer-events: none;
  background: repeating-linear-gradient(-60deg,
    transparent 0 5px,
    rgba(255, 255, 255, 0.3) 5px 10px,
    transparent 10px 15px,
    rgba(255, 255, 255, 0.3) 15px 20px
  );
  background-size: 100% 30px;
`;

const processTagsService = new ProcessTagsService();

const ProcessTimeline = ({ container, size, withStripes = false, openCameraView, showContextMenu = true, ...rest }) => {
  const [timelineTags, setTimelineTags] = useState([]);
  const [tagForm] = useForm({
    onSubmit: async (values, { mode }) => {
      if (!container) return;
      
      let tagsData = [...timelineTags];
      if (mode === FormMode.create) {
        // create
        const payload = { ...values };
        payload.tag = payload.tag.join('/');
        processTagsService.create(payload).then(tagData => {
          if (!tagData) return;
          const newTag = { ...tagData };
          newTag.tag = newTag.tag.split('/');
          tagsData = [...tagsData, newTag];
          setTimelineTags(tagsData);
        });
      } else {
        // update
        const payload = { ...values };
        payload.tag = payload.tag.join('/');
        processTagsService.update(payload).then(tagData => {
          if (!tagData) return;
          const updatedTag = { ...tagData };
          updatedTag.tag = updatedTag.tag.split('/');
          tagsData = tagsData.map(t => {
            if (t.id === updatedTag.id) {
              return updatedTag;
            }
            return t;
          });
          setTimelineTags(tagsData);
        });
      }

      tagForm.reset();
    }
  });
  const [tagNameOptions] = useState(() => {
    return [
      {
        label: 'Restroom Break',
        value: 'restroom_break',
      },
      {
        label: 'Receiving Issues',
        value: 'receiving_issues',
        children: [
          { label: 'Distro', value: 'distor' },
          { label: 'Item Dimensions', value: 'item_dimensions' },
          { label: 'UPCs', value: 'upcs' },
          { label: 'Mixed', value: 'mixed' },
        ]
      },
      {
        label: 'Equipment',
        value: 'equipment',
        children: [
          { label: 'No Carts', value: 'no_carts' },
          { label: 'No Trailers', value: 'no_trailers' },
        ]
      },
      {
        label: 'Special Handling',
        value: 'special_handling',
        children: [
          { label: 'Mixed', value: 'mixed' },
          { label: 'Dunnage/Wrapped', value: 'dunnage_wrapped' },
          { label: 'Avalanche', value: 'avalanche' },
          { label: 'Non-Prime', value: 'non_prime' },
        ]
      },
      {
        label: 'GLS Outage',
        value: 'gls_outage',
      },
      {
        label: 'Container Move',
        value: 'container_move',
        children: [
          { label: 'Door to Door', value: 'door_to_door' },
          { label: 'Alignment', value: 'alignment' },
        ]
      },
    ]
  });
  const [segments, setSegments] = useState({ data: [], labels: [], min: 0, max: undefined });

  useEffect(() => {
    if (container) {
      const { unloading } = container;
      const processId = unloading.id;
      const dockedAt = unloading.docked_at;
      const undockedAt = unloading.undocked_at || Math.floor(new Date().getTime() / 1000);

      if (processId && dockedAt && undockedAt) {
        processTagsService.loadAll({
          process_id: processId,
          from_ts: dockedAt,
          to_ts: undockedAt,
          include_employees: 0,
        })
          .then(data => {
            if (!data) return;
            setTimelineTags(data.tags.map(tag => {
              const mapped = { ...tag };
              mapped.tag = mapped.tag.split('/');
              return mapped;
            }));
          });
      }
    }
  }, [container]);

  useEffect(() => {
    if (container) {
      const { timeline, unloading } = container;

      const timelineStartTs = unloading.docked_at;
      let timelineEndTs = Math.round(new Date().getTime() / 1000);
      const unloadingStartedAt = unloading.started_at || null;
      const unloadingEndedAt = unloading.ended_at || null;
      const unloadingUndockedAt = unloading.undocked_at || null;
      const processId = unloading.id;
      const tzOffset = unloading.timezone_offset;

      // end of timeline must be undock ts or last timeline segments end time
      // if no segments in the timeline than it is the current time
      if (unloading.undocked_at) {
        timelineEndTs = unloading.undocked_at;
      } else if (timeline.length > 0) {
        const lastSegment = timeline[timeline.length - 1];
        timelineEndTs = Math.round(lastSegment.end / 1000);
      }
  
      let newSegments = { ...segments };
      newSegments.max = timelineEndTs - timelineStartTs;

      // Keep timelineEndTs unchanged when timeline tags change
      // if (!newSegments.max) {
      //   newSegments.max = timelineEndTs - timelineStartTs;
      // } else {
      //   timelineEndTs = timelineStartTs + newSegments.max;
      // }

      const onSegmentMenuItemClicked = (action, item) => {
        const startTs = Math.floor(item.start / 1000);
        const endTs = Math.floor(item.end / 1000);
  
        switch(action) {
          case 'addtag':
            tagForm.setExtraValues({
              process_id: item.processId,
              from_ts: startTs,
              to_ts: endTs,
            });
            tagForm.create();
          break;
  
          case 'edittag':
            tagForm.setExtraValues({
              id: item.tag.id,
              process_id: item.processId,
              from_ts: startTs,
              to_ts: endTs,
            });
            tagForm.setFieldsValue({
              tag: item.tag.tag,
            });
            tagForm.edit();
          break;
  
          case 'deletetag':
            processTagsService.remove(item.tag)
              .then((data) => {
                if (data) {
                  const newTimelineTags = timelineTags.filter(tag => tag.id !== item.tag.id);
                  setTimelineTags(newTimelineTags);
                }
              });
          break;
  
          case 'cameraview':
            openCameraView(startTs, endTs);
          break;
  
          default:
            // 
        }
      };

      newSegments.data = createSegments(
        timeline,
        timelineTags,
        timelineStartTs,
        timelineEndTs,
        tzOffset,
        processId,
        tagNameOptions,
        onSegmentMenuItemClicked,
        showContextMenu,
        size,
      );

      newSegments.labels = createLabels(
        timelineStartTs,
        timelineEndTs,
        unloadingStartedAt,
        unloadingEndedAt,
        unloadingUndockedAt,
        tzOffset,
        timeline
      );

      setSegments(newSegments);
    } else {
      setSegments({ data: [], labels: [], min: 0, max: undefined });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, timelineTags, openCameraView]);

  return (
    <>
      <Row style={{ width: '100%' }}>
        <Col span={24}>
          <TimelineBar
            min={segments.min}
            max={segments.max}
            segments={segments.data}
            borderShape="rounded"
            trailStyle={{
              height: size ? ({ small: '16px', large: '32px' })[size] : '24px'
            }}
            {...rest}
          />
          {withStripes ? <Stripes /> : null}
        </Col>
      </Row>
      {segments.labels.length ? (
        <Row style={{ paddingTop: '4px', minHeight: '40px' }}>
          <Col span={24}>
            {segments.labels.map((label, i) => {
              const [date, time] = label.text.split(' at ');
              return (
                <TimelineLabel key={i} style={label.styles}>
                  <Text style={{ color: '#45494D', fontSize: '14px', lineHeight: '22px', whiteSpace: 'nowrap' }}>
                    {date}
                  </Text>
                  <Text style={{ color: '#45494D', fontSize: '13px', lineHeight: '18px', whiteSpace: 'nowrap' }}>
                    {time}
                  </Text>
                  {label.subtext ? (
                    <Text style={{ color: '#A4B0BA', fontSize: '13px', lineHeight: '18px', whiteSpace: 'nowrap' }}>
                      {`(${label.subtext})`}
                    </Text>
                  ) : null}
                </TimelineLabel>
              );
            })}
          </Col>
        </Row>
      ) : null}
      <FormModal
        title={getAddTagModalTitle(tagForm, container ? container.unloading.timezone_offset : undefined)}
        form={tagForm}
        submitText={tagForm.mode === FormMode.create ? 'Add Tag' : 'Update Tag'}
        onClose={() => tagForm.reset()}
        width="522px"
      >
        <Form
          form={tagForm}
          layout="vertical"
          hideRequiredMark={true}
          onFinish={tagForm.onSubmit}
        >
          <Form.Item
            label="Tag Name"
            name="tag"
            rules={[
              {
                required: true,
                message: 'Tag name is required',
              },
            ]}
          >
            <Cascader options={tagNameOptions} placeholder="Select Tag" />
          </Form.Item>
        </Form>
      </FormModal>
    </>
  );
};

const StyledMenu = styled(Menu)`
  width: 150px;

  & .ant-dropdown-menu-item {
    padding-left: 16px;
  }
`;


const SegmentMenu = ({ hasTag,  ...rest }) => {
  return (
    <StyledMenu {...rest}>
      {!hasTag ? (
        <Menu.Item key="addtag" icon={<Icon name="tag" size={16} />}>Add Tag</Menu.Item>
      ) : [
        <Menu.Item key="edittag" icon={<Icon name="edit-2" size={16} />}>Edit Tag</Menu.Item>,
        <Menu.Item key="deletetag" icon={<Icon name="trash-2" color="#F5222D" size={16} />}>Delete Tag</Menu.Item>
      ]}
      <Menu.Item key="cameraview" icon={<Icon name="video" size={16} />}>View Camera</Menu.Item>
    </StyledMenu>
  );
};

const SegmentTooltipTitle = ({ fromTs, toTs, tzOffset, processId, segmentTag, allTags, isLowAccuracy, segmentColor, dtFormat = 'HH:mm' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [employees, setEmployees] = useState([]);

  const segmentTagLabels = [];
  if (segmentTag && segmentTag.tag) {
    segmentTag.tag.reduce((options, value) => {
      const option = options.find(o => o.value === value);
      if (!option) {
        return [];
      } else {
        segmentTagLabels.push(option.label)
      }
      return option.children;
    }, allTags);
  }


  useEffect(() => {
    if (processId && fromTs && toTs) {
      setIsLoading(true);

      processTagsService.loadAll({
        process_id: processId,
        from_ts: fromTs,
        to_ts: toTs,
        include_employees: 1,
      })
      .then(data => {
        if (!data && data.employees) return;
        setEmployees(data.employees);
      })
      .finally(() => {
        setIsLoading(false);
      });
    }
    
  }, [processId, toTs, fromTs]);

  const lowAccEl = isLowAccuracy ? (
    <Text style={{ fontSize: '13px', lineHeight: '22px', color: '#fff' }}>
      Low image quality
    </Text>
  ) : null;

  const tagsEl = segmentTag ? (
    <Text style={{ fontSize: '13px', lineHeight: '22px', color: '#fff' }}>
      {segmentTagLabels.join(' - ')}
    </Text>
  ) : null;

  const nonActivityColors = [timelineColors.black, timelineColors.red, timelineColors.empty];
  const isActiveSegment = !nonActivityColors.includes(segmentColor);
  
  const employeesEl = isActiveSegment ? employees.map((employee, i) => (
    <Text key={i} style={{ fontSize: '13px', lineHeight: '22px', color: '#fff', opacity: '0.8' }}>
      {employee.full_name}
    </Text>
  )) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Text style={{ fontSize: '14px', lineHeight: '18px', color: '#fff' }}>
        {`${formatDate(fromTs * 1000, dtFormat, { tzOffset })} - ${formatDate(toTs * 1000, dtFormat, { tzOffset })}`}
      </Text>
      {!isLoading ? (
        <>
          {lowAccEl}
          {tagsEl}
          {employeesEl}
        </>
      ) : (
        <SegmentTooltipSpin size="small" />
      )}
    </div>
  );
};

const getAddTagModalTitle = (tagForm, tzOffset) => {
  let title = '';
  if (tagForm.mode === FormMode.create) {
    title = 'Add tag for ';
  } else {
    title = 'Edit tag for '
  }

  if (tagForm.extraValues.from_ts && tagForm.extraValues.to_ts) {
    title += formatDate(tagForm.extraValues.from_ts * 1000, timeFormat, { tzOffset });
    title += ' - ';
    title += formatDate(tagForm.extraValues.to_ts * 1000, timeFormat, { tzOffset });
  }
  return title;
};

const createSegments = (timeline, tags, timelineStartTs, timelineEndTs, tzOffset, processId, allTags, onSegmentMenuItemClicked, showContextMenu, size) => {
  const duration = timelineEndTs - timelineStartTs;
  const dayStart = toTimezone(new Date(timelineStartTs*1000), tzOffset).getDay();
  const dayEnd = toTimezone(new Date(timelineEndTs*1000), tzOffset).getDay();
  
  const dtFormat = (duration >= 24 * 3600 || (dayStart !== dayEnd)) ? `${dateMonthDayFormat} 'at' ${timeFormat}` : timeFormat;
  
  const iconScale = size ? ({ small: 1, large: 1.8 })[size] : 1.4;

  const createSegment = (segmentData) => {
    const startTs = Math.floor(segmentData.start / 1000);
    const endTs = Math.floor(segmentData.end / 1000);

    const tag = tags.find(t => t.from_ts >= startTs && t.to_ts <= endTs);
    const hasTag = !!tag;
    const isLowAccuracy = segmentData.is_low_accuracy === 1;
    const showTagIcon = hasTag && ((endTs - startTs) / duration) > 0.05;
    const showArrowLeft = ((timelineEndTs - startTs) / duration) < 0.05;
    const showLowImgQuality = isLowAccuracy && ((endTs - startTs) / duration) > 0.05;

    const segmentEl = (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        {showTagIcon ? (
          <Icon
            name="tag"
            color="#fff"
            size={13}
            strokeWidth="3"
          />
        ) : null}
        {showLowImgQuality ? (
          <Icon
            name="image"
            size="16px"
            fill="#CF1322"
            color="white"
            strokeWidth="3"
            style={{
              position: 'absolute',
              backgroundColor: '#CF1322',
              borderRadius: '50%',
              width: '26px',
              height: '24px',
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
              transform: `scale(${iconScale})`,
              zIndex: 1,
            }}
          />
        ) : null}
      </div>
    );

    return {
      color: timelineColors[segmentData.color],
      value: endTs - startTs,
      tooltip: {
        title: () => (
          <SegmentTooltipTitle
            fromTs={startTs}
            toTs={endTs}
            processId={processId}
            segmentTag={tag}
            dtFormat={dtFormat}
            allTags={allTags}
            tzOffset={tzOffset}
            isLowAccuracy={isLowAccuracy}
            segmentColor={timelineColors[segmentData.color]}
          />
        ),
        trigger: 'hover',
        placement: showArrowLeft ? 'topRight' : 'top',
        arrowPointAtCenter: true,
      },
      render: () => {
        if (!showContextMenu) {
          return segmentEl;
        }

        return (
          <Dropdown
            overlay={() => (
              <SegmentMenu
                hasTag={hasTag}
                onClick={({ key }) => {
                  if (typeof onSegmentMenuItemClicked === 'function') {
                    onSegmentMenuItemClicked(key, {
                      ...segmentData,
                      processId,
                      tag,
                    });
                  }
                }}
              />
            )}
            trigger="click"
            placement="bottomLeft"
          >
            {segmentEl}
          </Dropdown>
        );
      }
    };
  }

  return timeline.map(createSegment);
};

const createLabels = (timelineStartTs, timelineEndTs, startedAt, endedAt, undockedAt, tzOffset, timeline) => {
  let labels = [];

  const totalDuration = timelineEndTs - timelineStartTs;
  const dayStart = toTimezone(new Date(timelineStartTs*1000), tzOffset).getDay();
  const dayEnd = toTimezone(new Date(timelineEndTs*1000), tzOffset).getDay();
  
   const dtFormat = (totalDuration >= 24 * 3600 || (dayStart !== dayEnd)) ? `${dateMonthDayFormat} 'at' ${timeFormat}` : timeFormat;
  const labelStartTs = Math.round(timelineStartTs / 3600) * 3600;
  const labelEndTs = Math.round(timelineEndTs / 3600) * 3600;

  console.debug('date format:'+dtFormat);

  labels.push({
    text: formatDate(timelineStartTs * 1000, dtFormat, { tzOffset }),
    subtext: 'Docked',
    styles: {
      left: '0%',
    }
  });

 let labelInterval = 10 * 60;
  while (true) {
    const nrOfPoints = Math.round((labelEndTs - labelStartTs) / labelInterval);
    if (nrOfPoints <= 6) {
      break;
    }
    labelInterval *= 2;
  }

  // eslint-disable-next-line
  let showStartTime = false;
  if (startedAt) {

    //if (diffToStart / totalDuration > 0.1 && diffToEnd / totalDuration > 0.3) {
      let perc = (startedAt - timelineStartTs) * 100 / totalDuration;
      if(perc !== 13)
        perc = 13;
      
      labels.push({
        text: formatDate(startedAt * 1000, dtFormat, { tzOffset }),
        subtext: 'Started',
        styles: {
          left: `${perc}%`,
        }
      });
      showStartTime = true;
    //}
  }

// eslint-disable-next-line
  let showEndTime = false;
  if (endedAt) {

    //if (diffToStart / totalDuration > 0.1 /*&& diffToEnd / totalDuration > 0.1*/) {
      let perc = (endedAt - timelineStartTs) * 100 / totalDuration;
      if(perc !== 87)
        perc = 87;

      labels.push({
        text: formatDate(endedAt * 1000, dtFormat, { tzOffset }),
        subtext: 'Finished',
        styles: {
          left: `${perc}%`,
        }
      });
      showEndTime = true;
    //}
  }

  let labelPoint = timelineStartTs + (timelineEndTs-timelineStartTs)/2;
  if(startedAt)
  {  
    labelPoint = startedAt + (timelineEndTs-startedAt)/2;

    if(endedAt)
      labelPoint = startedAt + (endedAt-startedAt)/2;
  }

  labels.push({
    text: formatDate(labelPoint * 1000, dtFormat, { tzOffset }),
    styles: {
      left: `50%`,
    }
  });

/*
  for (
    let labelPoint = labelStartTs + labelInterval;
    labelPoint < labelEndTs;
    labelPoint += labelInterval
  ) {

    if (showStartTime && Math.abs(startedAt - labelPoint) < labelInterval) continue;
    if (showEndTime && Math.abs(endedAt - labelPoint) < labelInterval) continue;
    if (labelPoint - (labelInterval / 2) <= timelineStartTs) continue;
    if (labelPoint + (labelInterval / 2) >= timelineEndTs) continue;

    const perc = (labelPoint - timelineStartTs) * 100 / totalDuration;

    labels.push({
      text: formatDate(labelPoint * 1000, dtFormat, { tzOffset }),
      styles: {
        left: `${perc}%`,
      }
    });
  }*/

  if (undockedAt) {
    labels.push({
      text: formatDate(timelineEndTs * 1000, dtFormat, { tzOffset }),
      subtext: 'Undocked',
      styles: {
        left: '100%',
      }
    });
  } else {
    labels.push({
      text: formatDate(timelineEndTs * 1000, dtFormat, { tzOffset }),
      subtext: 'Now',
      styles: {
        left: '100%',
      }
    });
  }
  

  return labels;
}

export default ProcessTimeline;
